import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TimelineIcon from "@material-ui/icons/Timeline";
import WhatshotIcon from "@material-ui/icons/Whatshot";
import SearchIcon from "@material-ui/icons/Search";
import DashboardIcon from "@material-ui/icons/Dashboard";
import PersonIcon from "@material-ui/icons/Person";
import utils from "../../universal/utils";

// Recharts is added as a dependency in this change set.
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  periodSticky: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  kpiCard: {
    height: "100%",
  },
  kpiNumber: {
    fontSize: "2.15rem",
    fontWeight: "bold",
    lineHeight: 1.1,
  },
  kpiLabel: {
    fontSize: "0.95rem",
    lineHeight: 1.35,
  },
  kpiNote: {
    fontSize: "0.85rem",
    lineHeight: 1.4,
    display: "block",
    marginTop: theme.spacing(0.5),
  },
  kpiDelta: {
    fontSize: "0.85rem",
    lineHeight: 1.4,
    display: "block",
    marginTop: theme.spacing(0.75),
    fontWeight: 600,
  },
  section: {
    marginTop: theme.spacing(3),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },
  periodLine: {
    fontSize: "0.85rem",
    lineHeight: 1.35,
  },
  ltrText: {
    direction: "ltr",
    unicodeBidi: "plaintext",
    display: "inline-block",
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 240,
    gap: theme.spacing(2),
  },
  chip: {
    marginRight: theme.spacing(1),
  },
  tableCell: {
    fontSize: "0.95rem",
    lineHeight: 1.4,
  },
  tableHeadCell: {
    fontSize: "0.95rem",
    lineHeight: 1.4,
    fontWeight: 700,
  },
});

const basePath = utils.getEdgeBasePath();

const PERIOD_STORAGE_KEY = "admin_analytics_period_v1";

function safeParseJson(raw) {
  if (!raw || typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

function isValidISODateString(s) {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function readSavedPeriod() {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage?.getItem(PERIOD_STORAGE_KEY);
  const parsed = safeParseJson(raw);
  if (!parsed || typeof parsed !== "object") return null;

  const { preset, from, to } = parsed;
  if (typeof preset !== "string") return null;
  if (!isValidISODateString(from) || !isValidISODateString(to)) return null;
  return { preset, from, to };
}

function savePeriod({ preset, from, to }) {
  if (typeof window === "undefined") return;
  if (typeof preset !== "string" || !isValidISODateString(from) || !isValidISODateString(to)) return;
  try {
    window.localStorage?.setItem(PERIOD_STORAGE_KEY, JSON.stringify({ preset, from, to }));
  } catch (_e) {
    // Ignore storage failures (quota/private mode)
  }
}

const HEBREW = {
  periodLabel: "תקופה",
  from: "מתאריך",
  to: "עד תאריך",
  apply: "החל",
  custom: "מותאם…",
  periodLine: "תקופה נבחרת",
  currentPeriod: "נוכחי",
  previousPeriod: "קודם",
  loading: "טוען נתונים...",
  failedToLoad: "שגיאה בטעינת הנתונים",
  noData: "אין נתונים",
  vsPriorPeriod: "לעומת התקופה הקודמת",
  change: "שינוי",
  showMore: "הצג עוד",
  showLess: "הצג פחות",

  presets: {
    today: "היום",
    yesterday: "אתמול",
    lastWeek: "שבוע שעבר",
    thisWeek: "השבוע",
    thisMonth: "החודש",
    lastMonth: "חודש שעבר",
    last3Months: "3 החודשים האחרונים",
    thisYear: "השנה",
    lastYear: "שנה שעברה",
    allTime: "כל הזמנים",
    custom: "מותאם",
  },

  kpis: {
    impressions: { label: "חשיפות", note: "מספר חשיפות/צפיות במוצרים שנרשמו במטריקות." },
    adds: { label: "הוספה לעגלה", note: "מספר הפעמים שמוצר נוסף לעגלה." },
    removes: { label: "הסרה מהעגלה", note: "מספר הפעמים שמוצר הוסר מהעגלה." },
    checkoutInit: { label: "תחילת צ'קאאוט", note: "מספר הפעמים שבהן התחיל תהליך תשלום (Checkout init)." },
    checkoutStage1: { label: "מעבר שלב 1", note: "מספר הפעמים שבהן עברו את שלב 1 בצ'קאאוט." },
    paidOrders: { label: "הזמנות (שהושלמו)", note: "כמות הזמנות שהושלמו בתקופה (כרטיס + העברה בנקאית)." },
    abandonedCheckouts: { label: "נטישות צ'קאאוט", note: "תחילות צ'קאאוט שלא הסתיימו בהזמנה בתקופה (הערכה)." },
    rateAddPerImpression: { label: "הוספה/חשיפה", note: "המרה: הוספות לעגלה חלקי חשיפות." },
    rateCheckoutPerAdd: { label: "צ'קאאוט/הוספה", note: "המרה: תחילת צ'קאאוט חלקי הוספות לעגלה." },
    rateStage1PerCheckout: { label: "שלב1/צ'קאאוט", note: "המרה: מעבר שלב 1 חלקי תחילת צ'קאאוט." },
    ratePaidPerCheckout: { label: "הושלם/צ'קאאוט", note: "המרה: הזמנות שהושלמו חלקי תחילת צ'קאאוט." },
    rateAbandonedPerCheckout: { label: "נטישה/צ'קאאוט", note: "שיעור נטישה: נטישות צ'קאאוט חלקי תחילות צ'קאאוט." },
  },

  sections: {
    sources: "מקורות הוספה לעגלה (עיקריים)",
    trendsDaily: "מגמות (יומי)",
    trendsLegendTitle: "פירוט אירועים",
    topProducts: "מוצרים מובילים (הוספה לעגלה)",
    emergingProducts: "מוצרים בצמיחה (שינוי בהוספה לעגלה)",
    topSizes: "מידות חיפוש מובילות (יומי)",
    emergingSizesNotStocked: "מידות בצמיחה (לא במלאי)",
    emergingSizesInStock: "מידות בצמיחה (במלאי)",
    abandonedWithContact: "נטישות צ'קאאוט עם פרטי קשר",
  },
  sources: {
    list_page: "עמוד רשימה",
    product_page: "עמוד מוצר",
  },

  table: {
    product: "מוצר",
    ean: "EAN",
    count: "כמות",
    current: "נוכחי (בתקופה הנבחרת)",
    baseline: "בסיס (בתקופה הקודמת)",
    delta: "שינוי (נוכחי − בסיס)",
    growthPct: "שינוי %",
    size: "מידה",
    searches: "חיפושים",
  },

  tableExplainers: {
    emerging: "נוכחי = כמות בתקופה הנבחרת. בסיס = כמות בתקופה הקודמת (באותו אורך). שינוי = נוכחי − בסיס. אחוז שינוי = שינוי ÷ בסיס.",
  },

  trends: {
    PRODUCT_IMPRESSION: {
      label: "חשיפות",
      desc: "מספר חשיפות/צפיות במוצרים.",
    },
    PRODUCT_ADD_TO_CART: {
      label: "הוספה לעגלה",
      desc: "מספר הפעמים שמוצר נוסף לעגלה.",
    },
    PRODUCT_REMOVE_FROM_CART: {
      label: "הסרה מהעגלה",
      desc: "מספר הפעמים שמוצר הוסר מהעגלה.",
    },
    CHECKOUT_INIT: {
      label: "תחילת צ'קאאוט",
      desc: "מספר הפעמים שבהן התחיל תהליך התשלום.",
    },
    CHECKOUT_PASSED_STAGE_1: {
      label: "צ'קאאוט שלב 1",
      desc: "מספר הפעמים שבהן עברו את שלב 1 בתהליך התשלום.",
    },
  },
};

function formatDateISO(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function getPresetRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const startOfWeekSunday = (d) => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    x.setDate(x.getDate() - x.getDay()); // Sunday
    return x;
  };

  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

  if (preset === "today") {
    const from = formatDateISO(today);
    return { from, to: from };
  }
  if (preset === "yesterday") {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    const from = formatDateISO(y);
    return { from, to: from };
  }
  if (preset === "thisWeek") {
    const fromD = startOfWeekSunday(today);
    return { from: formatDateISO(fromD), to: formatDateISO(today) };
  }
  if (preset === "last7") {
    const fromD = new Date(today);
    fromD.setDate(fromD.getDate() - 6);
    return { from: formatDateISO(fromD), to: formatDateISO(today) };
  }
  if (preset === "lastWeek") {
    const end = new Date(today);
    end.setDate(end.getDate() - 1);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return { from: formatDateISO(start), to: formatDateISO(end) };
  }
  if (preset === "thisMonth") {
    const fromD = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: formatDateISO(fromD), to: formatDateISO(today) };
  }
  if (preset === "lastMonth") {
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return { from: formatDateISO(startOfMonth(lastMonth)), to: formatDateISO(endOfMonth(lastMonth)) };
  }
  if (preset === "last3Months") {
    const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    return { from: formatDateISO(start), to: formatDateISO(today) };
  }
  if (preset === "thisYear") {
    const fromD = new Date(today.getFullYear(), 0, 1);
    return { from: formatDateISO(fromD), to: formatDateISO(today) };
  }
  if (preset === "lastYear") {
    const fromD = new Date(today.getFullYear() - 1, 0, 1);
    const toD = new Date(today.getFullYear() - 1, 11, 31);
    return { from: formatDateISO(fromD), to: formatDateISO(toD) };
  }
  if (preset === "allTime") {
    return { from: "1970-01-01", to: formatDateISO(today) };
  }
  return { from: formatDateISO(today), to: formatDateISO(today) };
}

function shiftRangeBack({ from, to }) {
  // Shift a range backwards by its (inclusive) duration in days.
  // Example: 2026-01-01..2026-01-07 (7 days) -> 2025-12-25..2025-12-31
  const fromD = new Date(`${from}T00:00:00Z`);
  const toD = new Date(`${to}T00:00:00Z`);
  const msPerDay = 24 * 60 * 60 * 1000;
  const days = Math.max(1, Math.round((toD - fromD) / msPerDay) + 1);
  const baselineTo = new Date(fromD.getTime() - msPerDay);
  const baselineFrom = new Date(baselineTo.getTime() - msPerDay * (days - 1));
  return { from: formatDateISO(baselineFrom), to: formatDateISO(baselineTo) };
}

function getPresetLabel(preset) {
  switch (preset) {
    case "today":
      return HEBREW.presets.today;
    case "yesterday":
      return HEBREW.presets.yesterday;
    case "lastWeek":
      return HEBREW.presets.lastWeek;
    case "thisWeek":
      return HEBREW.presets.thisWeek;
    case "thisMonth":
      return HEBREW.presets.thisMonth;
    case "lastMonth":
      return HEBREW.presets.lastMonth;
    case "last3Months":
      return HEBREW.presets.last3Months;
    case "thisYear":
      return HEBREW.presets.thisYear;
    case "lastYear":
      return HEBREW.presets.lastYear;
    case "allTime":
      return HEBREW.presets.allTime;
    case "custom":
      return HEBREW.presets.custom;
    default:
      return HEBREW.presets.custom;
  }
}

function buildSeriesPoints(seriesRows) {
  // seriesRows: [{ _id: {date,kind}, sample }]
  // Convert to: [{ date, PRODUCT_ADD_TO_CART: n, ... }]
  const byDate = new Map();
  for (const row of seriesRows || []) {
    const date = row?._id?.date;
    const kind = row?._id?.kind;
    if (!date || !kind) continue;
    if (!byDate.has(date)) byDate.set(date, { date });
    byDate.get(date)[kind] = row.sample || 0;
  }
  return Array.from(byDate.values()).sort((a, b) => (a.date > b.date ? 1 : -1));
}

function daysBetweenInclusive(from, to) {
  const fromD = new Date(`${from}T00:00:00Z`);
  const toD = new Date(`${to}T00:00:00Z`);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.max(1, Math.round((toD - fromD) / msPerDay) + 1);
}

function formatISODateUTC(d) {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function getWeekStartSundayUTC(dateISO) {
  const d = new Date(`${dateISO}T00:00:00Z`);
  const day = d.getUTCDay(); // 0=Sun
  d.setUTCDate(d.getUTCDate() - day);
  return formatISODateUTC(d);
}

function bucketizeSeriesPoints(points, bucket) {
  // points: [{ date: YYYY-MM-DD, KIND: n, ... }]
  // bucket: 'day' | 'week' | 'month'
  const out = new Map(); // key -> aggregated row
  for (const p of points || []) {
    const date = p?.date;
    if (!date) continue;

    let key = date;
    if (bucket === "week") key = getWeekStartSundayUTC(date);
    if (bucket === "month") key = String(date).slice(0, 7); // YYYY-MM

    if (!out.has(key)) out.set(key, { date: key });
    const row = out.get(key);
    for (const [k, v] of Object.entries(p)) {
      if (k === "date") continue;
      const n = Number(v);
      row[k] = (row[k] || 0) + (Number.isFinite(n) ? n : 0);
    }
  }
  return Array.from(out.values()).sort((a, b) => (a.date > b.date ? 1 : -1));
}

class AdminAnalyticsDashboard extends React.Component {
  constructor(props) {
    super(props);
    const saved = readSavedPeriod();
    const preset = saved?.preset || "lastMonth";
    const range = saved?.from && saved?.to ? { from: saved.from, to: saved.to } : getPresetRange(preset);
    const baseline = shiftRangeBack(range);
    this.state = {
      loading: true,
      error: null,
      preset,
      from: range.from,
      to: range.to,
      baselineFrom: baseline.from,
      baselineTo: baseline.to,
      overview: null,
      baselineOverview: null,
      topAdds: [],
      emergingAdds: [],
      topSizes: [],
      emergingSizesInStock: [],
      emergingSizesNotStocked: [],
      abandonedWithContactDaily: [],
      abandonedWithContactMonthly: [],
      abandonedWithContactLeads: [],
      abandonedWithContactLeadsError: null,
      expandedTables: {},
    };
  }

  componentDidMount() {
    this.loadAll();
  }

  loadAll = async () => {
    const { from, to, baselineFrom, baselineTo } = this.state;
    this.setState({ loading: true, error: null });

    try {
      const adminToken =
        typeof window !== "undefined" ? window.localStorage?.getItem("admin_token") : null;
      const adminHeaders = adminToken ? { "x-auth-token": adminToken } : {};

      const [
        overviewRes,
        baselineOverviewRes,
        topAddsRes,
        emergingRes,
        sizesRes,
        emergingSizesInStockRes,
        emergingSizesNotStockedRes,
        abandonedDailyRes,
        abandonedMonthlyRes,
        abandonedLeadsRes,
      ] = await Promise.all([
        fetch(`${basePath}/api/v1/admin/analytics/overview?from=${from}&to=${to}`),
        fetch(`${basePath}/api/v1/admin/analytics/overview?from=${baselineFrom}&to=${baselineTo}`),
        fetch(
          `${basePath}/api/v1/admin/analytics/products/top?from=${from}&to=${to}&kind=PRODUCT_ADD_TO_CART&limit=25`
        ),
        fetch(
          `${basePath}/api/v1/admin/analytics/products/emerging?from=${from}&to=${to}&baselineFrom=${baselineFrom}&baselineTo=${baselineTo}&kind=PRODUCT_ADD_TO_CART&limit=25`
        ),
        fetch(`${basePath}/api/v1/admin/analytics/sizes/top?from=${from}&to=${to}&limit=25`),
        fetch(
          `${basePath}/api/v1/admin/analytics/sizes/emerging?from=${from}&to=${to}&baselineFrom=${baselineFrom}&baselineTo=${baselineTo}&limit=25&stocked=1`
        ),
        fetch(
          `${basePath}/api/v1/admin/analytics/sizes/emerging?from=${from}&to=${to}&baselineFrom=${baselineFrom}&baselineTo=${baselineTo}&limit=25&stocked=0`
        ),
        fetch(`${basePath}/api/v1/admin/analytics/abandoned-with-contact?from=${from}&to=${to}&bucket=day`),
        fetch(`${basePath}/api/v1/admin/analytics/abandoned-with-contact?from=${from}&to=${to}&bucket=month`),
        fetch(`${basePath}/api/v1/admin/analytics/abandoned-with-contact/leads?from=${from}&to=${to}&limit=200`, {
          headers: adminHeaders,
        }),
      ]);

      const overviewJson = await overviewRes.json();
      const baselineOverviewJson = await baselineOverviewRes.json();
      const topAddsJson = await topAddsRes.json();
      const emergingJson = await emergingRes.json();
      const sizesJson = await sizesRes.json();
      const emergingSizesInStockJson = await emergingSizesInStockRes.json();
      const emergingSizesNotStockedJson = await emergingSizesNotStockedRes.json();
      const abandonedDailyJson = await abandonedDailyRes.json();
      const abandonedMonthlyJson = await abandonedMonthlyRes.json();
      const abandonedLeadsJson = await abandonedLeadsRes.json();

      if (!overviewJson.ok) throw new Error(overviewJson.error || "Failed to load overview");
      if (!baselineOverviewJson.ok)
        throw new Error(baselineOverviewJson.error || "Failed to load baseline overview");
      if (!topAddsJson.ok) throw new Error(topAddsJson.error || "Failed to load top products");
      if (!emergingJson.ok) throw new Error(emergingJson.error || "Failed to load emerging products");
      if (!sizesJson.ok) throw new Error(sizesJson.error || "Failed to load sizes");
      if (!emergingSizesInStockJson.ok) throw new Error(emergingSizesInStockJson.error || "Failed to load emerging sizes (stocked)");
      if (!emergingSizesNotStockedJson.ok) throw new Error(emergingSizesNotStockedJson.error || "Failed to load emerging sizes (not stocked)");
      if (!abandonedDailyJson.ok) throw new Error(abandonedDailyJson.error || "Failed to load abandoned-with-contact (daily)");
      if (!abandonedMonthlyJson.ok) throw new Error(abandonedMonthlyJson.error || "Failed to load abandoned-with-contact (monthly)");
      // Leads can fail if token is missing/expired; do not fail the entire dashboard.
      const leadsOk = Boolean(abandonedLeadsJson && abandonedLeadsJson.ok);

      this.setState({
        overview: overviewJson.overview,
        baselineOverview: baselineOverviewJson.overview,
        topAdds: topAddsJson.items || [],
        emergingAdds: emergingJson.items || [],
        topSizes: sizesJson.items || [],
        emergingSizesInStock: emergingSizesInStockJson.items || [],
        emergingSizesNotStocked: emergingSizesNotStockedJson.items || [],
        abandonedWithContactDaily: abandonedDailyJson.items || [],
        abandonedWithContactMonthly: abandonedMonthlyJson.items || [],
        abandonedWithContactLeads: leadsOk ? abandonedLeadsJson.items || [] : [],
        abandonedWithContactLeadsError: leadsOk ? null : abandonedLeadsJson?.error || "No access to leads (missing/expired token)",
      });
    } catch (err) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  toggleTable = (key) => {
    this.setState((prev) => ({
      expandedTables: { ...prev.expandedTables, [key]: !prev.expandedTables[key] },
    }));
  };

  renderSignedPctChange = (pct) => {
    const { theme } = this.props;
    const neutral = theme?.palette?.text?.secondary || "rgba(0,0,0,0.6)";
    const positive = theme?.palette?.success?.main || "#2e7d32";
    const negative = theme?.palette?.error?.main || "#d32f2f";

    if (pct === null || pct === undefined) {
      return <span style={{ color: neutral, fontWeight: 700 }}>—</span>;
    }

    const n = Number(pct);
    if (!Number.isFinite(n)) {
      return <span style={{ color: neutral, fontWeight: 700 }}>—</span>;
    }

    const sign = n > 0 ? "+" : "";
    const color = n > 0 ? positive : n < 0 ? negative : neutral;
    return <span style={{ color, fontWeight: 800 }}>{`${sign}${n}%`}</span>;
  };

  applyPreset = (preset) => {
    if (preset === "custom") {
      savePeriod({ preset, from: this.state.from, to: this.state.to });
      this.setState({ preset });
      return;
    }
    const range = getPresetRange(preset);
    const baseline = shiftRangeBack(range);
    savePeriod({ preset, from: range.from, to: range.to });
    this.setState(
      { preset, from: range.from, to: range.to, baselineFrom: baseline.from, baselineTo: baseline.to },
      this.loadAll
    );
  };

  renderDateRangeInline = (from, to) => {
    const { classes } = this.props;
    return <span className={classes.ltrText}>{`${from} – ${to}`}</span>;
  };

  renderPeriodInfo = ({ compact = false } = {}) => {
    const { classes } = this.props;
    const { preset, from, to, baselineFrom, baselineTo } = this.state;

    const typographyProps = compact
      ? { variant: "body2", color: "textSecondary", className: classes.periodLine }
      : { variant: "body2", color: "textSecondary" };

    return (
      <Typography {...typographyProps}>
        {HEBREW.periodLine}: {getPresetLabel(preset)}{" "}
        ({this.renderDateRangeInline(from, to)}){" "}
        | {HEBREW.previousPeriod}: ({this.renderDateRangeInline(baselineFrom, baselineTo)})
      </Typography>
    );
  };

  getTrendsBucket = () => {
    const { preset, from, to } = this.state;
    // Explicit behavior by preset (as requested)
    if (preset === "thisMonth" || preset === "lastMonth") return "week";
    if (preset === "thisYear" || preset === "lastYear" || preset === "allTime") return "month";

    // Sensible defaults for others / custom
    const days = daysBetweenInclusive(from, to);
    if (days > 180) return "month";
    if (days > 31) return "week";
    return "day";
  };

  renderTrendsDateTick = (bucket) => (props) => {
    // Force LTR rendering for date-like labels in RTL pages
    const { x, y, payload } = props;
    const value = payload?.value || "";
    const label = bucket === "month" ? `${value}` : `${value}`;
    return (
      <text x={x} y={y} dy={16} textAnchor="middle" style={{ fontSize: 12 }} direction="ltr" unicodeBidi="plaintext">
        {label}
      </text>
    );
  };

  renderKpis() {
    const { classes, theme } = this.props;
    const { overview, baselineOverview } = this.state;
    if (!overview) return null;

    const k = overview.kpis || {};
    const bk = baselineOverview?.kpis || {};

    const computeDeltaPct = (currentValue, baselineValue) => {
      const c = Number(currentValue);
      const b = Number(baselineValue);
      if (!Number.isFinite(c) || !Number.isFinite(b) || b === 0) return null;
      return Math.round(((c - b) / b) * 1000) / 10; // 0.1%
    };

    const renderDelta = (deltaPct) => {
      if (deltaPct === null) return "—";
      const sign = deltaPct > 0 ? "+" : "";
      return `${sign}${deltaPct}%`;
    };

    const deltaStyle = (deltaPct) => {
      const neutral = theme?.palette?.text?.secondary || "rgba(0,0,0,0.6)";
      const positive = theme?.palette?.success?.main || "#2e7d32";
      const negative = theme?.palette?.error?.main || "#d32f2f";
      if (deltaPct === null || deltaPct === 0) return { color: neutral };
      return { color: deltaPct > 0 ? positive : negative };
    };

    const completedOrdersValue = k.completedOrders ?? k.paidOrders ?? 0;
    const baselineCompletedOrdersValue = bk.completedOrders ?? bk.paidOrders ?? 0;

    const abandonedCheckoutsValue = k.abandonedCheckouts ?? 0;
    const baselineAbandonedCheckoutsValue = bk.abandonedCheckouts ?? 0;

    const items = [
      {
        key: "paidOrders",
        label: HEBREW.kpis.paidOrders.label,
        value: completedOrdersValue,
        note: HEBREW.kpis.paidOrders.note,
        deltaPct: computeDeltaPct(completedOrdersValue, baselineCompletedOrdersValue),
      },
      {
        key: "abandonedCheckouts",
        label: HEBREW.kpis.abandonedCheckouts.label,
        value: abandonedCheckoutsValue,
        note: HEBREW.kpis.abandonedCheckouts.note,
        deltaPct: computeDeltaPct(abandonedCheckoutsValue, baselineAbandonedCheckoutsValue),
      },
      {
        key: "impressions",
        label: HEBREW.kpis.impressions.label,
        value: k.impressions || 0,
        note: HEBREW.kpis.impressions.note,
        deltaPct: computeDeltaPct(k.impressions || 0, bk.impressions || 0),
      },
      {
        key: "adds",
        label: HEBREW.kpis.adds.label,
        value: k.adds || 0,
        note: HEBREW.kpis.adds.note,
        deltaPct: computeDeltaPct(k.adds || 0, bk.adds || 0),
      },
      {
        key: "removes",
        label: HEBREW.kpis.removes.label,
        value: k.removes || 0,
        note: HEBREW.kpis.removes.note,
        deltaPct: computeDeltaPct(k.removes || 0, bk.removes || 0),
      },
      {
        key: "checkoutInit",
        label: HEBREW.kpis.checkoutInit.label,
        value: k.checkoutInit || 0,
        note: HEBREW.kpis.checkoutInit.note,
        deltaPct: computeDeltaPct(k.checkoutInit || 0, bk.checkoutInit || 0),
      },
      {
        key: "checkoutStage1",
        label: HEBREW.kpis.checkoutStage1.label,
        value: k.checkoutStage1 || 0,
        note: HEBREW.kpis.checkoutStage1.note,
        deltaPct: computeDeltaPct(k.checkoutStage1 || 0, bk.checkoutStage1 || 0),
      },
    ];

    const rates = [
      {
        key: "rateAddPerImpression",
        label: HEBREW.kpis.rateAddPerImpression.label,
        value: `${k.rateAddPerImpressionPct || 0}%`,
        note: HEBREW.kpis.rateAddPerImpression.note,
        deltaPct: computeDeltaPct(k.rateAddPerImpressionPct || 0, bk.rateAddPerImpressionPct || 0),
      },
      {
        key: "rateCheckoutPerAdd",
        label: HEBREW.kpis.rateCheckoutPerAdd.label,
        value: `${k.rateCheckoutInitPerAddPct || 0}%`,
        note: HEBREW.kpis.rateCheckoutPerAdd.note,
        deltaPct: computeDeltaPct(k.rateCheckoutInitPerAddPct || 0, bk.rateCheckoutInitPerAddPct || 0),
      },
      {
        key: "rateStage1PerCheckout",
        label: HEBREW.kpis.rateStage1PerCheckout.label,
        value: `${k.rateStage1PerCheckoutInitPct || 0}%`,
        note: HEBREW.kpis.rateStage1PerCheckout.note,
        deltaPct: computeDeltaPct(
          k.rateStage1PerCheckoutInitPct || 0,
          bk.rateStage1PerCheckoutInitPct || 0
        ),
      },
      {
        key: "ratePaidPerCheckout",
        label: HEBREW.kpis.ratePaidPerCheckout.label,
        value: `${k.ratePaidPerCheckoutInitPct || 0}%`,
        note: HEBREW.kpis.ratePaidPerCheckout.note,
        deltaPct: computeDeltaPct(k.ratePaidPerCheckoutInitPct || 0, bk.ratePaidPerCheckoutInitPct || 0),
      },
      {
        key: "rateAbandonedPerCheckout",
        label: HEBREW.kpis.rateAbandonedPerCheckout.label,
        value: `${k.abandonmentRatePct || 0}%`,
        note: HEBREW.kpis.rateAbandonedPerCheckout.note,
        deltaPct: computeDeltaPct(k.abandonmentRatePct || 0, bk.abandonmentRatePct || 0),
      },
    ];

    return (
      <Paper className={classes.section}>
        <Box p={2}>
          <div className={classes.sectionHeader}>
            <DashboardIcon />
            <Box className={classes.sectionHeaderText}>
              <Typography variant="h6">מדדי ליבה</Typography>
              {this.renderPeriodInfo({ compact: true })}
            </Box>
          </div>
          <Divider />
        </Box>

        <Box p={2} pt={1}>
          <Grid container spacing={2}>
            {items.map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i.key}>
                <Card className={classes.kpiCard}>
                  <CardContent>
                    <Typography variant="body1" color="textSecondary" className={classes.kpiLabel}>
                      {i.label}
                    </Typography>
                    <Typography className={classes.kpiNumber}>{i.value}</Typography>
                    <Typography variant="body2" color="textSecondary" className={classes.kpiNote}>
                      {i.note}
                    </Typography>
                    <Typography className={classes.kpiDelta} style={{ ...deltaStyle(i.deltaPct) }}>
                      {HEBREW.vsPriorPeriod}: {renderDelta(i.deltaPct)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {rates.map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i.key}>
                <Card className={classes.kpiCard}>
                  <CardContent>
                    <Typography variant="body1" color="textSecondary" className={classes.kpiLabel}>
                      {i.label}
                    </Typography>
                    <Typography className={classes.kpiNumber}>{i.value}</Typography>
                    <Typography variant="body2" color="textSecondary" className={classes.kpiNote}>
                      {i.note}
                    </Typography>
                    <Typography className={classes.kpiDelta} style={{ ...deltaStyle(i.deltaPct) }}>
                      {HEBREW.vsPriorPeriod}: {renderDelta(i.deltaPct)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
    );
  }

  renderTrends() {
    const { classes } = this.props;
    const { overview } = this.state;
    if (!overview) return null;

    const bucket = this.getTrendsBucket();
    const dailyPoints = buildSeriesPoints(overview.series);
    const points = bucketizeSeriesPoints(dailyPoints, bucket);
    const seriesMeta = [
      { key: "PRODUCT_IMPRESSION", color: "#1976d2" },
      { key: "PRODUCT_ADD_TO_CART", color: "#2e7d32" },
      { key: "CHECKOUT_INIT", color: "#ed6c02" },
      { key: "CHECKOUT_PASSED_STAGE_1", color: "#9c27b0" },
      { key: "PRODUCT_REMOVE_FROM_CART", color: "#d32f2f" },
    ].map((s) => ({
      ...s,
      label: HEBREW.trends[s.key]?.label || s.key,
      desc: HEBREW.trends[s.key]?.desc || "",
    }));

    return (
      <Paper className={classes.section}>
        <Box p={2}>
          <div className={classes.sectionHeader}>
            <TimelineIcon />
            <Box className={classes.sectionHeaderText}>
              <Typography variant="h6">{HEBREW.sections.trendsDaily}</Typography>
              {this.renderPeriodInfo({ compact: true })}
            </Box>
          </div>
          <Divider />
          <Box mt={1}>
            <Typography variant="body2" color="textSecondary" className={classes.periodLine}>
              תצוגה:{" "}
              <span className={classes.ltrText}>
                {bucket === "day" ? "Daily" : bucket === "week" ? "Weekly" : "Monthly"}
              </span>
            </Typography>
          </Box>
          <Box mt={2} style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={this.renderTrendsDateTick(bucket)} />
                <YAxis />
                <Tooltip />
                <Legend />
                {seriesMeta.map((l) => (
                  <Line
                    key={l.key}
                    type="monotone"
                    dataKey={l.key}
                    name={l.label}
                    stroke={l.color}
                    dot={false}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>

          <Box mt={2}>
            <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 6 }}>
              {HEBREW.sections.trendsLegendTitle}
            </Typography>
            {seriesMeta.map((s) => (
              <Box key={s.key} display="flex" alignItems="flex-start" mb={0.75}>
                <Box
                  width={10}
                  height={10}
                  borderRadius={2}
                  mr={1}
                  mt={"6px"}
                  style={{ backgroundColor: s.color, flex: "0 0 auto" }}
                />
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 700, lineHeight: 1.35 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ lineHeight: 1.4 }}>
                    {s.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    );
  }

  renderSourceBreakdown() {
    const { classes } = this.props;
    const { overview } = this.state;
    if (!overview) return null;
    const raw = overview.addToCartBySource || [];
    if (!raw.length) return null;

    const totalAdds = Number(overview?.kpis?.adds || 0) || 0;
    const bySource = new Map(raw.map((r) => [r.source, Number(r.sample || 0) || 0]));

    const mainSources = [
      { key: "list_page", label: HEBREW.sources.list_page, sample: bySource.get("list_page") || 0 },
      { key: "product_page", label: HEBREW.sources.product_page, sample: bySource.get("product_page") || 0 },
    ];

    return (
      <Paper className={classes.section}>
        <Box p={2}>
          <div className={classes.sectionHeader}>
            <TrendingUpIcon />
            <Box className={classes.sectionHeaderText}>
              <Typography variant="h6">{HEBREW.sections.sources}</Typography>
              {this.renderPeriodInfo({ compact: true })}
            </Box>
          </div>
          <Divider />
          <Box mt={1.5}>
            {mainSources.every((s) => s.sample === 0) ? (
              <Typography variant="body2" color="textSecondary">
                {HEBREW.noData}
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {mainSources.map((s) => {
                  const pct = totalAdds > 0 ? Math.round((s.sample / totalAdds) * 1000) / 10 : 0;
                  return (
                    <Grid item xs={12} sm={6} key={s.key}>
                      <Card variant="outlined">
                        <CardContent style={{ paddingBottom: 16 }}>
                          <Typography variant="body2" color="textSecondary" className={classes.kpiLabel}>
                            {s.label}
                          </Typography>
                          <Typography className={classes.kpiNumber}>{s.sample}</Typography>
                          <Typography variant="body2" color="textSecondary" className={classes.kpiNote}>
                            חלק יחסי מהוספות לעגלה:{" "}
                            <span className={classes.ltrText}>{`${pct}%`}</span>
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        </Box>
      </Paper>
    );
  }

  renderAbandonedWithContact() {
    const { classes } = this.props;
    const { abandonedWithContactDaily, abandonedWithContactMonthly, abandonedWithContactLeads, abandonedWithContactLeadsError } = this.state;
    const daily = Array.isArray(abandonedWithContactDaily) ? abandonedWithContactDaily : [];
    const monthly = Array.isArray(abandonedWithContactMonthly) ? abandonedWithContactMonthly : [];

    if (!daily.length && !monthly.length) return null;

    const renderChart = (title, data) => {
      return (
        <Box mt={2} style={{ height: 260 }}>
          <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 6 }}>
            {title}
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" tick={this.renderTrendsDateTick(title.includes("חודשי") ? "month" : "day")} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="leadsWithContact" name="השאירו פרטים (ייחודי)" stroke="#1976d2" dot={false} strokeWidth={2} />
              <Line
                type="monotone"
                dataKey="abandonedWithContactApprox"
                name="נטשו (הערכה)"
                stroke="#d32f2f"
                dot={false}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      );
    };

    return (
      <Paper className={classes.section}>
        <Box p={2}>
          <div className={classes.sectionHeader}>
            <PersonIcon />
            <Box className={classes.sectionHeaderText}>
              <Typography variant="h6">{HEBREW.sections.abandonedWithContact}</Typography>
              {this.renderPeriodInfo({ compact: true })}
              <Typography variant="body2" color="textSecondary" className={classes.periodLine}>
                נספר לפי אירוע <span className={classes.ltrText}>CHECKOUT_PASSED_STAGE_1</span> עם טלפון תקין, ייחודי לפי טלפון בכל יום/חודש.
              </Typography>
            </Box>
          </div>
          <Divider />
          {renderChart("יומי", daily)}
          {renderChart("חודשי", monthly)}

          <Box mt={2}>
            <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 6 }}>
              לידים למעקב (פרטי קשר)
            </Typography>
            {abandonedWithContactLeadsError ? (
              <Typography variant="body2" color="textSecondary">
                לא ניתן להציג לידים: {abandonedWithContactLeadsError}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>שם</TableCell>
                      <TableCell className={classes.tableHeadCell}>טלפון</TableCell>
                      <TableCell className={classes.tableHeadCell}>אימייל</TableCell>
                      <TableCell className={classes.tableHeadCell}>תאריך אחרון</TableCell>
                      <TableCell className={classes.tableHeadCell}>מס׳ אירועים</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(abandonedWithContactLeads) && abandonedWithContactLeads.length ? (
                      abandonedWithContactLeads.map((r) => (
                        <TableRow key={r.phone}>
                          <TableCell className={classes.tableCell}>{r.name || "—"}</TableCell>
                          <TableCell className={classes.tableCell}>
                            <span className={classes.ltrText}>{r.phone || "—"}</span>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <span className={classes.ltrText}>{r.emailaddress || "—"}</span>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <span className={classes.ltrText}>{r.lastDate || "—"}</span>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <span className={classes.ltrText}>{String(r.eventsCount || 0)}</span>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" className={classes.tableCell}>
                          {HEBREW.noData}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Paper>
    );
  }

  getEmergingSizesExportUrl = (stocked) => {
    const { from, to, baselineFrom, baselineTo } = this.state;
    const stockedParam = stocked === null ? "" : `&stocked=${stocked ? "1" : "0"}`;
    return `${basePath}/api/v1/admin/analytics/sizes/emerging/export?from=${from}&to=${to}&baselineFrom=${baselineFrom}&baselineTo=${baselineTo}${stockedParam}`;
  };

  renderTable(tableKey, title, rows, columns, maxInitial = 10, subtitle = null, headerActions = null) {
    const { classes } = this.props;
    const expanded = Boolean(this.state.expandedTables?.[tableKey]);
    const visibleRows = expanded ? rows : rows.slice(0, maxInitial);
    return (
      <Paper className={classes.section}>
        <Box p={2}>
          <div className={classes.sectionHeader}>
            <WhatshotIcon />
            <Box className={classes.sectionHeaderText}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Typography variant="h6" style={{ flex: "1 1 auto", minWidth: 0 }}>
                  {title}
                </Typography>
                {headerActions && <Box style={{ flex: "0 0 auto" }}>{headerActions}</Box>}
              </Box>
              {this.renderPeriodInfo({ compact: true })}
              {subtitle && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 2, lineHeight: 1.4 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </div>
          <Divider />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c.key} className={classes.tableHeadCell}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" className={classes.tableCell}>
                    {HEBREW.noData}
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((r, idx) => (
                  <TableRow key={r.ean || `${idx}`}>
                    {columns.map((c) => (
                      <TableCell key={c.key} className={classes.tableCell}>
                        {c.render(r)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {rows.length > maxInitial && (
          <Box p={2} pt={1} display="flex" justifyContent="center">
            <Button variant="outlined" size="small" onClick={() => this.toggleTable(tableKey)}>
              {expanded ? HEBREW.showLess : HEBREW.showMore}
            </Button>
          </Box>
        )}
      </Paper>
    );
  }

  renderProductLabel(r) {
    const brand = r?.brand || "—";
    const name = r?.displayName || "—";
    const size = r?.displaySize || "—";
    return (
      <div>
        <Typography variant="body1" style={{ fontWeight: 700, lineHeight: 1.35 }}>
          {brand}
        </Typography>
        <Typography variant="body2" style={{ lineHeight: 1.4 }}>
          {name} <span style={{ color: "rgba(0,0,0,0.7)", fontWeight: 600 }}>{size}</span>
        </Typography>
      </div>
    );
  }

  sortEmergingByGrowthPct = (rows) => {
    const arr = Array.isArray(rows) ? [...rows] : [];
    arr.sort((a, b) => {
      const ag = Number(a?.growthPct);
      const bg = Number(b?.growthPct);
      const aHas = Number.isFinite(ag);
      const bHas = Number.isFinite(bg);

      // Growth % descending; null/invalid last
      if (aHas && bHas && bg !== ag) return bg - ag;
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;

      // Tie-breakers: delta desc, then currentSample desc (fallbacks for different payload shapes)
      const ad = Number(a?.delta ?? (a?.currentSample ?? 0) - (a?.baselineSample ?? 0));
      const bd = Number(b?.delta ?? (b?.currentSample ?? 0) - (b?.baselineSample ?? 0));
      if (Number.isFinite(ad) && Number.isFinite(bd) && bd !== ad) return bd - ad;

      const ac = Number(a?.currentSample ?? a?.sample ?? 0);
      const bc = Number(b?.currentSample ?? b?.sample ?? 0);
      if (Number.isFinite(ac) && Number.isFinite(bc) && bc !== ac) return bc - ac;

      return 0;
    });
    return arr;
  };

  render() {
    const { classes } = this.props;
    const {
      loading,
      error,
      preset,
      from,
      to,
      baselineFrom,
      baselineTo,
      topAdds,
      emergingAdds,
      topSizes,
      emergingSizesInStock,
      emergingSizesNotStocked,
    } =
      this.state;

    const emergingAddsSorted = this.sortEmergingByGrowthPct(emergingAdds);
    const emergingSizesInStockSorted = this.sortEmergingByGrowthPct(emergingSizesInStock);
    const emergingSizesNotStockedSorted = this.sortEmergingByGrowthPct(emergingSizesNotStocked);

    return (
      <div className={classes.root}>
        <Box className={classes.controls}>
          <TextField
            select
            label={HEBREW.periodLabel}
            value={preset}
            onChange={(e) => this.applyPreset(e.target.value)}
            size="small"
            style={{ minWidth: 220 }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="today">{HEBREW.presets.today}</MenuItem>
            <MenuItem value="yesterday">{HEBREW.presets.yesterday}</MenuItem>
            <MenuItem value="lastWeek">{HEBREW.presets.lastWeek}</MenuItem>
            <MenuItem value="thisWeek">{HEBREW.presets.thisWeek}</MenuItem>
            <MenuItem value="thisMonth">{HEBREW.presets.thisMonth}</MenuItem>
            <MenuItem value="lastMonth">{HEBREW.presets.lastMonth}</MenuItem>
            <MenuItem value="last3Months">{HEBREW.presets.last3Months}</MenuItem>
            <MenuItem value="thisYear">{HEBREW.presets.thisYear}</MenuItem>
            <MenuItem value="lastYear">{HEBREW.presets.lastYear}</MenuItem>
            <MenuItem value="allTime">{HEBREW.presets.allTime}</MenuItem>
            <MenuItem value="custom">{HEBREW.custom}</MenuItem>
          </TextField>

          {preset === "custom" && (
            <>
              <TextField
                label={HEBREW.from}
                type="date"
                value={from}
                onChange={(e) => this.setState({ from: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label={HEBREW.to}
                type="date"
                value={to}
                onChange={(e) => this.setState({ to: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Chip
                icon={<SearchIcon />}
                color="primary"
                label={HEBREW.apply}
                onClick={() => {
                  const baseline = shiftRangeBack({ from: this.state.from, to: this.state.to });
                  savePeriod({ preset: "custom", from: this.state.from, to: this.state.to });
                  this.setState({ baselineFrom: baseline.from, baselineTo: baseline.to }, this.loadAll);
                }}
                clickable
              />
            </>
          )}
        </Box>

        <Paper className={classes.periodSticky} elevation={0}>
          <Box p={1.5}>
            <Typography variant="body2" color="textSecondary">
              {HEBREW.currentPeriod}: {getPresetLabel(preset)} ({this.renderDateRangeInline(from, to)}) |{" "}
              {HEBREW.previousPeriod}: ({this.renderDateRangeInline(baselineFrom, baselineTo)})
            </Typography>
          </Box>
        </Paper>

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
            <Typography>{HEBREW.loading}</Typography>
          </div>
        ) : error ? (
          <Paper className={classes.section}>
            <Box p={2}>
              <Typography color="error">
                {HEBREW.failedToLoad}: {error}
              </Typography>
            </Box>
          </Paper>
        ) : (
          <>
            {this.renderKpis()}
            {this.renderTrends()}
            {this.renderSourceBreakdown()}
            {this.renderAbandonedWithContact()}

            {this.renderTable("topProducts", HEBREW.sections.topProducts, topAdds, [
              { key: "product", label: HEBREW.table.product, render: (r) => this.renderProductLabel(r) },
              { key: "ean", label: HEBREW.table.ean, render: (r) => <span className={classes.ltrText}>{r.ean}</span> },
              { key: "sample", label: HEBREW.table.count, render: (r) => r.sample },
            ])}

            {this.renderTable(
              "emergingProducts",
              HEBREW.sections.emergingProducts,
              emergingAddsSorted,
              [
              { key: "product", label: HEBREW.table.product, render: (r) => this.renderProductLabel(r) },
              { key: "ean", label: HEBREW.table.ean, render: (r) => <span className={classes.ltrText}>{r.ean}</span> },
              {
                  key: "changePct",
                  label: HEBREW.table.growthPct,
                  render: (r) => this.renderSignedPctChange(r.growthPct),
              },
              ],
              10,
              null
            )}

            {this.renderTable("topSizes", HEBREW.sections.topSizes, topSizes, [
              { key: "size", label: HEBREW.table.size, render: (r) => <span className={classes.ltrText}>{`${r.width}/${r.profile}R${r.rim}`}</span> },
              { key: "sample", label: HEBREW.table.searches, render: (r) => r.sample },
            ])}

            {this.renderTable(
              "emergingSizesNotStocked",
              HEBREW.sections.emergingSizesNotStocked,
              emergingSizesNotStockedSorted,
              [
              { key: "size", label: HEBREW.table.size, render: (r) => <span className={classes.ltrText}>{`${r.width}/${r.profile}R${r.rim}`}</span> },
              {
                  key: "changePct",
                  label: HEBREW.table.growthPct,
                  render: (r) => this.renderSignedPctChange(r.growthPct),
              },
              ],
              10,
              null,
              <Button
                variant="outlined"
                size="small"
                href={this.getEmergingSizesExportUrl(false)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Export (HTML/PDF)
              </Button>
            )}

            {this.renderTable(
              "emergingSizesInStock",
              HEBREW.sections.emergingSizesInStock,
              emergingSizesInStockSorted,
              [
              { key: "size", label: HEBREW.table.size, render: (r) => <span className={classes.ltrText}>{`${r.width}/${r.profile}R${r.rim}`}</span> },
              {
                  key: "changePct",
                  label: HEBREW.table.growthPct,
                  render: (r) => this.renderSignedPctChange(r.growthPct),
              },
              ],
              10,
              null,
              <Button
                variant="outlined"
                size="small"
                href={this.getEmergingSizesExportUrl(true)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Export (HTML/PDF)
              </Button>
            )}
          </>
        )}
      </div>
    );
  }
}

AdminAnalyticsDashboard.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(AdminAnalyticsDashboard);


